import { FC, useState } from 'react';

import { useSession } from '@eneco/auth';
import { useSelfServiceAccount } from '@eneco/auth-be-self-service';
import RichText from '@eneco/components/RichText/RichText';
import { deleteEnecoBeXapiSiteApiV1ProfileItsme } from '@eneco/dc-be/client';
import { useRouter } from '@eneco/next';
import { Box, Button, ButtonLink, Dialog, Stack, Text, TextLink } from '@eneco/sparky';
import { useTracking } from '@eneco/tracking';
import { useContent } from '@sitecore/common';
import { ProfileManagementLoginDetailsRendering } from '@sitecore/types/ProfileManagementLoginDetails';
import { signIn } from 'next-auth/react';
import { useSWRConfig } from 'swr';

import ReadWithAction from './ReadWithAction';
import { ChampionDisabledAction } from './ChampionDisabledAction';
import { ITSME_LINK__OKTA_ACCESS_TOKEN_KEY } from '../pages/my-eneco/profile/login-details/link-itsme/ProfileManagementLoginDetailsLinkItsme';

type ItsmeCardData = {
  hasItsmeLogin: boolean;
  hasLocalLogin: boolean;
};

type ItsmeDialogData = {
  // queryparam names
  queryParameterSuccess: string;
  queryParameterNotification: string;
  // param that describes which message to show
  valueNotificationUnlink: string;
  valueNotificationLink: string;
};

type ItsmeCardProps = {
  data: ItsmeCardData;
  dialogData: ItsmeDialogData;
};

export const ItsmeCard: FC<ItsmeCardProps> = ({ data, dialogData }) => {
  const { f } = useContent<ProfileManagementLoginDetailsRendering>();
  const { isImpersonating } = useSelfServiceAccount();
  let title: string;
  let text: string;
  let actionText: string;
  let showAction = true;

  const showLink = !data.hasItsmeLogin;

  if (showLink) {
    title = f('itsmeLoginCardTitle.value');
    text = f('itsmeLoginCardText.value');
    actionText = f('itsmeLoginCardLink.value.text');
  } else {
    title = f('itsmeLoginCardUnlinkTitle.value');
    text = f('itsmeLoginCardUnlinkText.value');
    actionText = f('itsmeLoginCardUnlinkLink.value.text');

    if (!data.hasLocalLogin) {
      showAction = false;
      actionText = f('itsmeLoginCardUnlinkActionUnsupportedNoPasswordText.value');
    }
  }

  const buildRedirectUrl = (success: boolean, ...params: string[]) => {
    return `${f('unlinkDialogLink.value.href')}?${dialogData.queryParameterSuccess}=${success}&${dialogData.queryParameterNotification}=${params.join(',')}`;
  };

  const [unlinkInProgress, setUnlinkInProgress] = useState(false);

  const [unlinkDialogIsOpen, setUnlinkDialogIsOpen] = useState<boolean>(false);

  const { mutate } = useSWRConfig();
  const { trackEventBE } = useTracking();
  const router = useRouter();
  const onSubmitUnlinkItsme = () => {
    setUnlinkInProgress(true);

    deleteEnecoBeXapiSiteApiV1ProfileItsme()
      .catch(err => {
        router.push(buildRedirectUrl(false, dialogData.valueNotificationUnlink));
      })
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .then(({ response }: any) => {
        if (response.ok) {
          setUnlinkInProgress(false);
          setUnlinkDialogIsOpen(false);
          trackEventBE('itsme_unlinking', { status: 'success' });
          mutate('session');
          router.push(buildRedirectUrl(true, dialogData.valueNotificationUnlink));
        } else {
          setUnlinkInProgress(false);
          setUnlinkDialogIsOpen(false);
          trackEventBE('itsme_unlinking', { status: 'failed', message: 'unexpected error occurred' });
          router.push(buildRedirectUrl(false, dialogData.valueNotificationUnlink));
        }
      });
  };

  const redirectUri = f('itsmeLoginCardLink.value.href');
  const { data: sessionData } = useSession();
  const onSubmitLinkItsme = () => {
    if (sessionData !== null) {
      sessionStorage.setItem(ITSME_LINK__OKTA_ACCESS_TOKEN_KEY, sessionData.accessToken || '');
      signIn('itsme', { callbackUrl: redirectUri });
    }
  };

  const canManageItsme = !isImpersonating();

  const itsmeAction = (
    <Box>
      {showAction && !showLink && (
        <Dialog
          title={f('unlinkDialogTitle.value')}
          description={<RichText html={f('unlinkDialogContent.value')} />}
          trigger={
            <TextLink emphasis="high">
              <RichText html={actionText} />
            </TextLink>
          }
          width="regular"
          isOpen={unlinkDialogIsOpen}
          setOpen={newValue => setUnlinkDialogIsOpen(newValue)}>
          <Stack direction={'row'} gap={'6'}>
            <Button action="primary" onClick={onSubmitUnlinkItsme} isLoading={unlinkInProgress}>
              <RichText html={f('unlinkDialogLink.value.text')} />
            </Button>
            <Dialog.Close asChild>
              <ButtonLink action="secondary">{f('unlinkDialogActionCancelText.value')}</ButtonLink>
            </Dialog.Close>
          </Stack>
        </Dialog>
      )}
      {showAction && showLink && (
        <TextLink emphasis="high" onClick={onSubmitLinkItsme}>
          <RichText html={actionText} />
        </TextLink>
      )}
      {!showAction && <RichText html={actionText} />}
    </Box>
  );

  return (
    <ReadWithAction title={title}>
      <Stack direction="column" alignX="start" gap="6">
        <Box>
          <Text size="BodyM">{text}</Text>
        </Box>
        {canManageItsme ? (
          itsmeAction
        ) : showAction ? (
          <ChampionDisabledAction>
            <TextLink emphasis="high">
              <RichText html={actionText} />
            </TextLink>
          </ChampionDisabledAction>
        ) : (
          <RichText html={actionText} />
        )}
      </Stack>
    </ReadWithAction>
  );
};
